<template>
    <div class="autocomplete control" :class="{size, 'is-expanded': expanded}">
        <uInput v-model="newValue"
            ref="input"
            :size="size"
            :loading="loading"
            :icon="icon"
            :icon-pack="iconPack"
            :maxlength="maxlength"
            autocomplete="off"
            v-bind="$attrs"
            @input="inputed"
            @on-focus="focused"
            @on-blur="blured"
            @blur="$emit('blur', $event)"
            @keyup.native.esc.prevent="isActive = false"
            @keydown.native.enter.prevent="enterPressed"
            @keydown.native.up.prevent="keyArrows('up')"
            @keydown.native.down.prevent="keyArrows('down')">
        </uInput>

        <transition name="fade">
            <div class="dropdown-menu"
                :class="{ 'is-opened-top': !isListInViewportVertically }"
                v-show="isActive && (visibleData.length > 0 || hasEmptySlot)"
                ref="dropdown">
                <div class="dropdown-content">
                    <a v-for="(option, index) in visibleData"
                        :key="index"
                        class="dropdown-item"
                        :class="{ 'is-hovered': option === hovered }"
                        @click="setSelected(option)">

                        <slot v-if="hasDefaultSlot" :option="option" :index="index"></slot>
                        <span v-else v-html="getValue(option, true)"></span>
                    </a>
                    <div v-if="data.length > maxResults"
                        class="dropdown-item is-disabled">
                        &hellip;
                    </div>
                    <div v-else-if="visibleData.length === 0"
                        class="dropdown-item is-disabled">
                        <slot name="empty"></slot>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import { getValueByPath, escapeRegExpChars } from '../../utils/helpers'
    import FormElementMixin from '../../utils/FormElementMixin'

    export default {
        name: 'Autocomplete',
        inheritAttrs: false,
        mixins: [FormElementMixin],
        props: {
            value: [Number, String],
            data: Array,
            field: {
                type: String,
                default: 'value'
            },
            maxResults: {
                type: [Number, String],
                default: 6
            },
            keepFirst: Boolean
        },
        data() {
            return {
                selected: null,
                hovered: null,
                isActive: false,
                newValue: this.value,
                isListInViewportVertically: true,
                _isAutocomplete: true,
                _elementRef: 'input',
                focuseStatus:false
            }
        },
        computed: {
            /**
             * White-listed items to not close when clicked.
             * Add input, dropdown and all children.
             */
            whiteList() {
                const whiteList = []
                whiteList.push(this.$refs.input)
                whiteList.push(this.$refs.dropdown)
                // Add all chidren from dropdown
                if (this.$refs.dropdown !== undefined) {
                    const children = this.$refs.dropdown.querySelectorAll('*')
                    for (const child of children) {
                        whiteList.push(child)
                    }
                }

                return whiteList
            },


            visibleData() {
                return this.data.length <= this.maxResults
                    ? this.data
                    : this.data.slice(0, this.maxResults)
            },

            hasDefaultSlot() {
                return !!this.$scopedSlots.default
            },



            hasEmptySlot() {
                return !!this.$slots.empty
            }
        },
        watch: {
            isActive(active) {
                if (active) {
                    this.calcDropdownInViewportVertical()
                } else {               
                    this.$nextTick(() => this.setHovered(null))
                    // Timeout to wait for the animation to finish before recalculating
                    setTimeout(() => {
                       this.calcDropdownInViewportVertical()
                    }, 100)
                }
            },
            newValue(value) {
                if(!this.focuseStatus) return;                              
                this.$emit('input', value)
                if (this.getValue(this.selected) !== value) this.setSelected(null, false)
                if (this.keepFirst && this.visibleData.length) {
                    this.$nextTick(() => {
                        if (this.hovered !== this.visibleData[0]) {
                            this.setHovered(this.visibleData[0])
                        }
                    })
                }
                this.isActive = true;
            },
            value(value) {
                this.newValue = value
                !this.isValid && this.$refs.input.checkHtml5Validity()
            }
        },
        methods: {
            setHovered(option) {
                if (option === undefined) return

                this.hovered = option
            },
            setSelected(option, closeDropdown = true) {
                if (option === undefined) return
                this.selected = option
                this.$emit('select', this.selected)
                this.focuseStatus=true;
                if (this.selected !== null) {
                    this.newValue = this.getValue(this.selected)
                }
                closeDropdown && this.$nextTick(() => { this.isActive = false, this.focuseStatus=false })
            },
            enterPressed() {
                if (this.hovered === null) return
                this.setSelected(this.hovered)
            },

            /**
             * Close dropdown if clicked outside.
             */
            clickedOutside(event) {
                if (this.whiteList.indexOf(event.target) < 0) this.isActive = false
            },


            getValue(option, isHighlight = false) {
                if (!option) return

                const value = typeof option === 'object'
                    ? getValueByPath(option, this.field)
                    : option

                const escapedValue = escapeRegExpChars(this.newValue)
                const regex = new RegExp(`(${escapedValue})`, 'gi')

                return isHighlight
                    ? value.replace(regex, '<b>$1</b>')
                    : value
            },


            calcDropdownInViewportVertical() {
                 this.data.splice(0,this.data.length); 
                this.$nextTick(() => {
                    const rect = this.$refs.dropdown.getBoundingClientRect()
                    this.isListInViewportVertically = (
                        rect.top >= 0 &&
                        rect.bottom <= (window.innerHeight ||
                            document.documentElement.clientHeight)
                    )
                })
            },


            keyArrows(direction) {
                const sum = direction === 'down' ? 1 : -1
                if (this.isActive) {
                    let index = this.visibleData.indexOf(this.hovered) + sum
                    index = index > this.visibleData.length - 1 ? 0 : index
                    index = index < 0 ? this.visibleData.length - 1 : index

                    this.setHovered(this.visibleData[index])
                } else {
                    this.isActive = true
                }
            },

            inputed(val){
                this.$emit('input', val);
                //if(this.value!=val) this.value=val;
            },

            focused(event) {
                if (this.getValue(this.selected) === this.newValue) this.focus();
                this.focuseStatus=true;
                if(this.value==""&&!this.isActive){
                    this.$emit('input', "");     
                    this.isActive=true;
                }else{
                    this.$emit('focus', event)
                }                       
            },

            blured(){
                this.focuseStatus=false;
            }

        },
        created() {
            if (typeof window !== 'undefined') {
                document.addEventListener('click', this.clickedOutside)
                window.addEventListener('resize', this.calcDropdownInViewportVertical)
            }
        },
        beforeDestroy() {
            if (typeof window !== 'undefined') {
                document.removeEventListener('click', this.clickedOutside)
                window.removeEventListener('resize', this.calcDropdownInViewportVertical)
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/Autocomplete/Autocomplete.vue